/*
 * yougi-variant-selects.js
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 *
 * sections/main-product.liquid renders its size picker as:
 *
 *     <variant-selects class="yougi-mp__variants" data-section-id="..." ...>
 *       <fieldset><legend>Size</legend>
 *         <input type="radio" name="Size" value="170g" checked> ...
 *       </fieldset>
 *       <script type="application/json">[ ...variant JSON... ]</script>
 *     </variant-selects>
 *
 * `<variant-selects>` is a Dawn-theme tag name. This store runs Maker 12.1.0,
 * whose components are `product-options-element`, `product-price-element`, etc.
 * Neither assets/theme.js nor assets/theme-async.js contains the string
 * "variant-selects", so the custom element was NEVER defined and the radios
 * were completely inert: checking a radio changed nothing, and the cart form's
 * hidden `id` input kept the first variant's id. A customer picking
 * "450g three-wick" (£45) added the 170g (£29) to their bag.
 *
 * This file supplies the missing element definition ONLY. It changes no
 * markup, no styling and no other theme file. It upgrades in place wherever a
 * <variant-selects> tag exists (product pages only), so it is inherently
 * scoped and is a no-op everywhere else.
 *
 * Added 2026-07-20 while fixing the silent wrong-variant add-to-cart bug.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('variant-selects')) return;

  /* ---- Shopify money formatting (standard implementation) ---------------- */

  function defaultTo(value, fallback) {
    return value === undefined || value === null ? fallback : value;
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ',');
    decimal = defaultTo(decimal, '.');
    if (isNaN(cents) || cents === null) return '0';
    var number = (cents / 100.0).toFixed(precision);
    var parts = number.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  function formatMoney(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';
    var match = formatString.match(placeholder);
    var value;
    switch (match && match[1]) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ','); break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholder, value);
  }

  function shopMoneyFormat() {
    var host = document.querySelector('[data-money-format]');
    return (host && host.getAttribute('data-money-format')) || '£{{amount}}';
  }

  // shop.money_format on this store is "<span class=money>\u00a3{{amount}}</span>",
  // i.e. it already carries its own markup, so it must be written as HTML.
  // (Writing it with textContent printed the literal tags on the page.)
  function setMoney(el, cents) {
    if (!el) return;
    el.innerHTML = formatMoney(cents, shopMoneyFormat());
  }

  /* ---- The element ------------------------------------------------------- */

  class YougiVariantSelects extends HTMLElement {
    connectedCallback() {
      if (this.dataset.yougiInited === '1') return;

      // Variant data is emitted by main-product.liquid as a JSON island.
      var json = this.querySelector('script[type="application/json"]');
      if (!json) return;
      try {
        this.variants = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }
      if (!Array.isArray(this.variants) || !this.variants.length) return;

      this.inputs = Array.prototype.slice.call(
        this.querySelectorAll('input[type="radio"]')
      );
      if (!this.inputs.length) return; // single-variant product: nothing to wire

      // Fieldsets are rendered one per product option, in option order.
      this.fieldsets = Array.prototype.slice.call(this.querySelectorAll('fieldset'));
      if (!this.fieldsets.length) this.fieldsets = [this];

      // Scope every lookup to this section so the sticky bar and any other
      // product section on the page are never clobbered by accident.
      this.root = this.closest('.shopify-section') || document;
      this.form = this.root.querySelector('form[action*="/cart/add"]');
      this.idInput = this.form && this.form.querySelector('input[name="id"]');
      this.priceEl = this.root.querySelector('.yougi-mp__price');
      this.compareEl = this.root.querySelector(
        '.yougi-mp__compare, .yougi-mp__price-compare, [data-compare-price]'
      );
      this.addButton = this.root.querySelector('[data-add-button]');
      this.addLabel = this.root.querySelector('[data-add-label]');
      this.addPrice = this.root.querySelector('[data-add-price]');
      this.stockNote = this.root.querySelector('.yougi-mp__stock-note');
      this.stockText = this.stockNote && this.stockNote.querySelector('span:not([class])');

      // Copy overrides can be supplied from the section; these fall back to the
      // strings currently configured on the main-product section settings.
      this.addLabelText = this.dataset.addLabel ||
        (this.addLabel ? this.addLabel.textContent.trim() : 'Add to bag');
      this.soldOutLabelText = this.dataset.soldOutLabel || 'Sold out';
      this.inStockMessage = this.dataset.stockMessage ||
        (this.stockText ? this.stockText.textContent.trim() : '');
      this.soldOutMessage = this.dataset.soldOutMessage ||
        'Currently sold out. Email us to be notified.';

      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);

      this.dataset.yougiInited = '1';

      // Sync once on load so a deep link / browser-restored radio is honoured.
      this.onChange();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    selectedOptions() {
      return this.fieldsets.map(function (fs) {
        var checked = fs.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    currentVariant() {
      var chosen = this.selectedOptions();
      return this.variants.find(function (v) {
        return chosen.every(function (value, i) {
          return value === null || v['option' + (i + 1)] === value;
        });
      }) || null;
    }

    onChange() {
      var variant = this.currentVariant();
      if (!variant) return;
      this.updateForm(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateStock(variant);
      this.updateSticky(variant);
      this.updateURL(variant);

      // Namespaced on purpose: yougi-sticky-atc.liquid listens for the bare
      // "variant:change" and reformats the price incorrectly (renders "Rs.").
      // The sticky bar is updated directly in updateSticky() instead.
      document.dispatchEvent(new CustomEvent('yougi:variant:change', {
        bubbles: true,
        detail: { variant: variant, sectionId: this.dataset.sectionId }
      }));
    }

    updateForm(variant) {
      // THE ACTUAL BUG FIX: keep the cart payload in step with the radios.
      if (this.idInput) this.idInput.value = variant.id;
    }

    updatePrice(variant) {
      setMoney(this.priceEl, variant.price);
      if (this.compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          setMoney(this.compareEl, variant.compare_at_price);
          this.compareEl.hidden = false;
        } else {
          this.compareEl.textContent = '';
          this.compareEl.hidden = true;
        }
      }
    }

    updateButton(variant) {
      if (!this.addButton) return;
      this.addButton.disabled = !variant.available;
      this.addButton.setAttribute('aria-disabled', String(!variant.available));
      this.addButton.dataset.variantAvailable = String(!!variant.available);
      if (this.addLabel) {
        this.addLabel.textContent = variant.available
          ? this.addLabelText
          : this.soldOutLabelText;
      }
      // Hide the "· £45.00" tail when there is nothing to buy.
      var sep = this.root.querySelector('.yougi-mp__add-sep');
      if (this.addPrice) {
        if (variant.available) {
          setMoney(this.addPrice, variant.price);
          this.addPrice.hidden = false;
          if (sep) sep.hidden = false;
        } else {
          this.addPrice.textContent = '';
          this.addPrice.hidden = true;
          if (sep) sep.hidden = true;
        }
      }
    }

    updateStock(variant) {
      if (!this.stockNote) return;
      this.stockNote.dataset.stockState = variant.available ? 'in' : 'out';
      if (this.stockText) {
        this.stockText.textContent = variant.available
          ? this.inStockMessage
          : this.soldOutMessage;
      }
    }

    updateSticky(variant) {
      var sticky = document.querySelector('.yougi-satc');
      if (!sticky) return;
      if (sticky.dataset.productId &&
          this.dataset.productId &&
          sticky.dataset.productId !== this.dataset.productId) return;

      // sections/yougi-sticky-atc.liquid has its own document-level change
      // listener that reformats the sticky price with a broken money format:
      // it renders "Rs.45.00" instead of "\u00a345.00" (its formatMoney falls back
      // to the old Shopify "Rs.{{amount}}" default because it never reads
      // shop.money_format). That is a PRE-EXISTING bug in that section and it
      // fires on any size change, with or without this file.
      // We write the sticky bar twice: once synchronously, and once on the next
      // tick so our correctly formatted price is the one that survives.
      // The proper fix belongs in yougi-sticky-atc.liquid.
      this.writeSticky(sticky, variant);
      var self = this;
      setTimeout(function () { self.writeSticky(sticky, variant); }, 0);
    }

    writeSticky(sticky, variant) {
      var size = sticky.querySelector('[data-satc-size]');
      if (size) size.textContent = variant.title;

      setMoney(sticky.querySelector('[data-satc-price]'), variant.price);

      var btn = sticky.querySelector('[data-satc-add]');
      if (btn) {
        btn.setAttribute('data-variant-id', variant.id);
        btn.disabled = !variant.available;
        btn.setAttribute('aria-disabled', String(!variant.available));
        var label = btn.querySelector('[data-satc-label]');
        if (label) {
          label.textContent = variant.available
            ? this.addLabelText
            : this.soldOutLabelText;
        }
      }
    }

    updateURL(variant) {
      // main-product.liquid sets data-update-url="false"; respect it.
      if (this.dataset.updateUrl === 'false') return;
      if (!variant.id || !window.history || !window.history.replaceState) return;
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  }

  customElements.define('variant-selects', YougiVariantSelects);
})();
